import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { formatText } from '../../common/TextFormatterProvider';
import { formatDisplayValue } from '../../../utils/tagValueUtils';
import { profileName } from '../../../utils/profileDisplay';
import { isTiedAllocation } from '../../../utils/uploadNames';

// One table staging file awaiting its data-profile decision. Mounted by the table
// door (SmartUploadFiles) and, with `tableChip` set, by the workspace files home.
// Bulk approve / learned-profile gating stay with the parent.
function AllocationCard({
  file, profiles, nameById, onProfileChange, onApprove, onDelete, tableChip, batchLabel,
}) {
  const activeProfiles = profiles || [];
  const proposedProfile = activeProfiles.find(p => p.id === file.proposed_data_profile) || { default_tags: {} };
  // Approve only offers what approval accepts: a real, active profile. The
  // allocator's 'No Match' sentinel, a deleted profile, or an unresolved tie
  // (no selection) all leave it disabled.
  const tied = isTiedAllocation(file);
  const tiedIds = tied ? file.tied_profile_ids : [];
  const selectionIsReal = activeProfiles.some(p => p.id === file.selected_data_profile);
  const tiedProfiles = activeProfiles.filter(p => tiedIds.includes(p.id));
  const otherProfiles = activeProfiles.filter(p => !tiedIds.includes(p.id));
  const matchedTags = file.matched_tags || {};
  const profileTags = proposedProfile.default_tags || {};
  const matchCount = Object.keys(profileTags).reduce((count, category) =>
    count + (matchedTags[category] === profileTags[category] ? 1 : 0), 0);
  const totalTags = Object.keys(profileTags).length || 1;
  // Attributes the workspace drop attached to this file — beside, not inside, the
  // profile match: they are asserted by the user, never inferred.
  const packAttributes = Object.entries(file.pack_attributes || {});

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ flexGrow: 1, mr: 1 }}>
            {file.fileName}
          </Typography>
          <IconButton
            size="small"
            onClick={() => onDelete(file.id)}
            sx={{ p: 0 }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>

        {(tableChip || batchLabel) && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            {tableChip && <Chip label={formatText(tableChip)} size="small" variant="outlined" />}
            {batchLabel && <Chip label={batchLabel} size="small" variant="outlined" />}
          </Box>
        )}

        {packAttributes.length > 0 && (
          <Tooltip title="Applied at upload — overrides the profile's value for this category">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              {packAttributes.map(([category, value]) => (
                <Chip
                  key={category}
                  label={`${formatText(category)}: ${formatDisplayValue(value)}`}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Box>
          </Tooltip>
        )}

        {file.status === 'analysing' || file.status === 'splitting' ? (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            <Typography variant="body2">
              {file.status === 'splitting' ? 'Splitting sheets...' : 'Analysing...'}
            </Typography>
            <CircularProgress size={16} sx={{ ml: 1 }} />
          </Box>
        ) : (
          <>
            {/* A Failed member is delete-only: its upload may already be final,
                so a recovery here could never finalize or fire automations. The
                way back is a fresh upload, which is its own batch (H91). */}
            {file.status === 'Failed' && (
              <>
                <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>
                  {file.error || 'Allocation failed.'}
                </Alert>
                <Typography variant="body2" color="text.secondary">
                  This file cannot be recovered here. Delete it and upload it again.
                </Typography>
              </>
            )}
            {/* A pending card carrying an error bounced back from approval (or
                parked by the auto-allocation mandatory-attribute pre-check). */}
            {file.status !== 'Failed' && file.error && (
              <Alert severity="warning" sx={{ mb: 1, py: 0.5 }}>
                {file.error}
              </Alert>
            )}
            {file.status !== 'Failed' && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" gutterBottom sx={{ mb: 0 }}>
                    Matched Profile: {matchCount}/{totalTags} ({tied ? 'Tied' : (nameById[file.proposed_data_profile] ?? file.proposed_data_profile ?? 'No Match')})
                  </Typography>
                  {tied && <Chip label="Tied" color="warning" size="small" />}
                </Box>
                {tied && (
                  <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 1 }}>
                    Matched {tiedIds.length} data profiles equally:{' '}
                    {tiedIds.map(id => nameById[id] ?? id).join(', ')} — choose one to approve.
                  </Typography>
                )}
                {!tied && activeProfiles.length === 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    This table has no data profiles yet — create one in the profiles sidebar, then approve.
                  </Typography>
                )}
                {Object.keys(profileTags).map(category => {
                  // Only nothing at all is unmatched: a matched No (false) is a
                  // value, and reads as No.
                  const matched = matchedTags[category];
                  const hasMatch = matched !== null && matched !== undefined;
                  return (
                    <Box key={category} sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <Typography variant="body2">{formatText(category)}: </Typography>
                      <Chip
                        label={hasMatch ? formatDisplayValue(matched) : 'Unmatched'}
                        color={hasMatch && matched === profileTags[category] ? 'success' : 'default'}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Box>
                  );
                })}
              </>
            )}
            {file.status !== 'Failed' && (
              <>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id={`select-profile-${file.id}`}>Select Data Profile</InputLabel>
              <Select
                labelId={`select-profile-${file.id}`}
                value={selectionIsReal ? file.selected_data_profile : ''}
                onChange={(e) => onProfileChange(file.id, e.target.value)}
                label="Select Data Profile"
              >
                {/* The tied candidates lead the list; every other profile follows. */}
                {tiedProfiles.map((profile) => (
                  <MenuItem key={profile.id} value={profile.id}>
                    {profileName(profile)}
                  </MenuItem>
                ))}
                {tiedProfiles.length > 0 && otherProfiles.length > 0 && (
                  <MenuItem disabled dense>Other profiles</MenuItem>
                )}
                {otherProfiles.map((profile) => (
                  <MenuItem key={profile.id} value={profile.id}>
                    {profileName(profile)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              size="small"
              sx={{ mt: 1, width: '100%' }}
              onClick={() => onApprove(file.id, file.selected_data_profile)}
              disabled={!selectionIsReal}
            >
              Approve
            </Button>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default AllocationCard;
